import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { poultryApi } from '../api/modules';
import { StatusPill } from '../components/ui/StatusPill';
import { PoultryDrawer } from '../drawers/PoultryDrawer';

const STAGES = [
  { key: 'cvo_review',     label: 'CVO Review',           owner: 'CVO',                        accent: 'bg-cream-50',    text: 'text-navy-800' },
  { key: 'pre_appraisal',  label: 'Pre-Appraisal',        owner: 'Mandal Committee',           accent: 'bg-cream-50',    text: 'text-navy-800' },
  { key: 'appraisal',      label: 'Directorate Appraisal', owner: 'Dir. Appraisal Committee',  accent: 'bg-saffron-50',  text: 'text-saffron-700' },
  { key: 'final_approval', label: 'Final Approval',       owner: 'Approval Cmte. + Pr. Sec.',  accent: 'bg-navy-50',     text: 'text-navy-800' },
];

export function PoultryPage() {
  const [stage, setStage] = useState<string>('any');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const summary = useQuery({ queryKey: ['poultry-summary'], queryFn: poultryApi.summary });
  const list = useQuery({
    queryKey: ['poultry-list', { stage, page }],
    queryFn: () => poultryApi.list({ page, page_size: 25, stage }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <Header breadcrumbs={['Modules', 'Poultry Approval']} />
      <div className="px-8 pt-7 pb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">Module M03</div>
          <h1 className="serif-display text-3xl text-navy-800 font-medium">Poultry Approval Workflow</h1>
          <p className="text-sm text-navy-800/60 mt-1">
            DPR submission → CVO review → Pre-appraisal → Directorate appraisal → Final approval
          </p>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Stage pipeline cards */}
        <div className="grid grid-cols-4 gap-3">
          {STAGES.map((s) => {
            const count = summary.data?.stages[s.key]?.count ?? 0;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => { setStage(s.key); setPage(1); }}
                className={`${s.accent} border border-cream-200 rounded-sm p-4 text-left transition hover:shadow-md ${stage === s.key ? 'ring-2 ring-saffron-500' : ''}`}
              >
                <div className={`text-[10px] uppercase tracking-widest ${s.text} font-semibold`}>{s.label}</div>
                <div className="text-[10px] text-navy-800/50 mt-1">{s.owner}</div>
                <div className={`font-numeric serif-display text-3xl ${s.text} font-medium mt-3`}>{count}</div>
                <div className="text-xs text-navy-800/60 mt-1">{count === 1 ? 'application' : 'applications'}</div>
              </button>
            );
          })}
        </div>

        {/* Filter chips */}
        <div className="bg-white border border-cream-200 rounded-sm p-4 flex items-center gap-3 flex-wrap text-xs">
          <span className="text-navy-800/60">Show stage:</span>
          <button type="button" onClick={() => { setStage('any'); setPage(1); }}
            className={`px-3 py-1.5 rounded-sm transition ${stage === 'any' ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}>
            All
          </button>
          {STAGES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => { setStage(s.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-sm transition ${stage === s.key ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-cream-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-cream-100 text-xs text-navy-800/60">
            {list.data ? `${list.data.data.length} of ${list.data.pagination.total} applications` : 'Loading…'}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50">
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">App ID</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Applicant</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Sector</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">District</th>
                <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">DPR (₹L)</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Stage</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading || !list.data ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-t border-cream-100"><td colSpan={7} className="px-3 py-3"><div className="h-3 bg-cream-100 rounded animate-pulse" /></td></tr>
                ))
              ) : (
                list.data.data.map((p) => (
                  <tr key={p.id} onClick={() => setSelected(p.id)} className="border-t border-cream-100 hover:bg-cream-50 cursor-pointer">
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">{p.id}</td>
                    <td className="px-3 py-2.5 font-medium text-navy-800">{p.applicant}</td>
                    <td className="px-3 py-2.5 text-navy-800/80">{p.sector}</td>
                    <td className="px-3 py-2.5 text-navy-800/70">{p.district}</td>
                    <td className="px-3 py-2.5 text-right font-numeric font-medium">{p.dpr_lakhs.toFixed(1)}</td>
                    <td className="px-3 py-2.5"><StatusPill status={p.stage} /></td>
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">
                      {new Date(p.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PoultryDrawer open={!!selected} code={selected} onClose={() => setSelected(null)} />
    </>
  );
}
